import { ReactElement, useEffect, useState } from 'react';
import { Formik } from 'formik';
import { Box, Grid, Typography } from '@mui/material';
import {
    AppFormikTextField,
    AppPrimaryButton,
} from 'shared/components/AppComponents';
import { UserSettingService } from 'services/UserSettingService';
import { ProfileActions } from 'state/profile/Action';
import { connect } from 'react-redux';
import { Utils } from 'shared/helpers';
import DateFnsAdapter from '@date-io/date-fns';
import { UserDetailFormScheme } from 'validators/UserDetailFormScheme';
import { useUpdateEffect } from 'react-use';
import { typeMap } from 'modules/home/manage-access/content/components/data/typeMap';

const dateFns = new DateFnsAdapter();

const UserDetailsForm = (props: any): ReactElement => {
    const { profileState, setProfileData } = props;
    const [profileForm, setProfileForm] = useState({} as ProfileInfo);

    const [isProfile, setBindProfile] = useState<boolean>(false);
    // Instance methods
    const bindProfile = (): void => {
        if (profileState) {
            const { userInfo } = profileState;
            setProfileForm({
                firstName: userInfo.name.first,
                lastName: userInfo.name.last,
                phoneNumber: userInfo.phoneNumber,
                role: typeMap[userInfo.role]?.[0],
                email: userInfo.email,
                createdAt: userInfo.createdAt,
            });
            // setInitialTouchedRecoveryEmail(
            //     userInfo.email === userInfo.recoveryEmail
            // );
        }
    };

    // API Calls
    const getProfile = (): void => {
        UserSettingService.getProfile()
            .then((response: any) => {
                const profileData = response.data.data.userInfo;
                /* eslint no-underscore-dangle: 0 */
                const mockData = {
                    userInfo: {
                        createdAt: profileData.createdAt,
                        name: profileData.name,
                        phoneNumber: profileData.phoneNumber
                            ? profileData.phoneNumber
                            : '',
                        email: profileData.email.id,
                        // recoveryEmail: profileData.recoveryEmail.id
                        //     ? profileData.recoveryEmail.id
                        //     : '',
                        registeredOn: profileData.registeredOn,
                        userId: profileData._id,
                        _id: profileData._id,
                        twoFactorAuthentication:
                            profileData.twoFactorAuthentication,
                        globalDashboardTemplateEnabled:
                            profileData.globalDashboardTemplateEnabled,
                        globalDashboardTemplateId:
                            profileData.globalDashboardTemplateId,
                        customDashboardTemplateEnabled:
                            profileData.customDashboardTemplateEnabled,
                        customDashboardTemplateId:
                            profileData.customDashboardTemplateId,
                        defaultDashboardTemplateId:
                            profileData.defaultDashboardTemplateId,
                        role: profileData.role,
                    },
                };
                setBindProfile(true);
                setProfileData(mockData);
            })
            .catch((response) => {
                setBindProfile(true);
                Utils.handleErrorResponse(response);
            });
    };
    const updateProfile = (formValues: any, callback: any): void => {
        UserSettingService.updateProfile(formValues)
            .then((response: any) => {
                Utils.handleSuccessResponse(response);
                callback(true);
            })
            .catch((error: any) => {
                Utils.handleErrorResponse(error);
                callback(false);
            });
    };

    // UI Actions
    const actionOnFormSubmit = (formValues: any, formik: any): void => {
        updateProfile(
            {
                phoneNumber: `${formValues.phoneNumber}`,
                firstName: formValues.firstName,
                lastName: formValues.lastName,
                // recoveryEmail: formValues.recoveryEmail,
                email: formValues.email,
            },
            () => {
                formik.setSubmitting(false);
            }
        );
    };

    // Hooks
    useEffect(() => {
        getProfile();
    }, []);

    useUpdateEffect(() => {
        if (isProfile) {
            bindProfile();
            setBindProfile(false);
        }
    }, [isProfile]);

    // UI Elements
    return (
        <Box className="p-10">
            <Box className="app-box-wrapper">
                <Box className="app-box-header align-center p-6">
                    <Typography className="title-left">
                        {profileForm.firstName} {profileForm.lastName}
                    </Typography>

                    <Box className="flex align-center">
                        <Typography className="title-right">
                            Registered since:{' '}
                            {profileForm.createdAt
                                ? dateFns.formatByString(
                                      dateFns.parseISO(profileForm.createdAt),
                                      'dd MMM yyyy'
                                  )
                                : ''}
                        </Typography>
                    </Box>
                </Box>

                <Box className="app-box-content">
                    <Formik
                        validationSchema={UserDetailFormScheme}
                        enableReinitialize
                        initialValues={profileForm}
                        validateOnMount
                        onSubmit={(formValues, formik) => {
                            actionOnFormSubmit(formValues, formik);
                        }}
                    >
                        {({ handleSubmit, isValid, isSubmitting }) => (
                            <form autoComplete="off">
                                <Box className="p-8">
                                    <Grid container spacing={5}>
                                        <Grid item xs={6}>
                                            <AppFormikTextField
                                                outerLabel
                                                label="First Name"
                                                name="firstName"
                                                inputProps={{
                                                    readOnly: true,
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <AppFormikTextField
                                                outerLabel
                                                label="Last Name"
                                                name="lastName"
                                                inputProps={{
                                                    readOnly: true,
                                                }}
                                            />
                                        </Grid>
                                        {false && (
                                            <Grid item xs={4}>
                                                <Typography
                                                    variant="body1"
                                                    className="text-secondary lato-regular"
                                                >
                                                    Phone number
                                                </Typography>
                                                <Box className="flex">
                                                    <AppFormikTextField
                                                        outerLabel
                                                        name="phoneNumber"
                                                        inputProps={{
                                                            readOnly: true,
                                                        }}
                                                    />
                                                </Box>
                                            </Grid>
                                        )}
                                        <Grid item xs={6}>
                                            <AppFormikTextField
                                                outerLabel
                                                label="Email"
                                                name="email"
                                                inputProps={{
                                                    readOnly: true,
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={6} display="none">
                                            <AppFormikTextField
                                                outerLabel
                                                label="Recovery Email *"
                                                name="recoveryEmail"
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography
                                                variant="body1"
                                                className="text-secondary lato-regular"
                                            >
                                                Role
                                            </Typography>
                                            <Box className="flex">
                                                <AppFormikTextField
                                                    outerLabel
                                                    name="role"
                                                    inputProps={{
                                                        readOnly: true,
                                                    }}
                                                />
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </Box>

                                {false && (
                                    <Box className="identity-preview-footer">
                                        <AppPrimaryButton
                                            title="Save & Update"
                                            isLoading={isSubmitting}
                                            loadingSize={22}
                                            loadingText="SAVING"
                                            disabled={!isValid || isSubmitting}
                                            onClick={handleSubmit}
                                            className="btn-small poppins-semi-bold f11"
                                        />
                                    </Box>
                                )}
                            </form>
                        )}
                    </Formik>
                </Box>
            </Box>
        </Box>
    );
};

const mapStateToProps = (state: any): any => ({
    profileState: state.profileState,
});

const mapDispatchToProps = (dispatch: any): any => ({
    setProfileData: (profileData: any) =>
        dispatch(ProfileActions.setProfileData(profileData)),
});

export default connect(mapStateToProps, mapDispatchToProps)(UserDetailsForm);
